import { useEffect, useState } from 'react';
import { ClipboardList, FileCheck, Plus, X, Pencil, Trash2, Lock, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import CredentialStatusBadge from '../../components/spray/CredentialStatusBadge';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useOrgTimezone, formatLocalDateInTz } from '../../utils/timezone';
import {
  listCertificationTypes,
  listCertificationCategories,
  createCertificationType,
  updateCertificationType,
  deactivateCertificationType,
  getCertificationReport,
  createCertification,
  updateCertification,
  type StaffCertificationType,
  type CertificationReportRow,
} from '../../services/staffCertification';

type AdminTab = 'register' | 'types';

// ============================================================================
// Main page
// ============================================================================

function CertificationsAdmin() {
  const navigate = useNavigate();
  // All hooks first — guards come after (per CLAUDE.md: admin/super_admin RBAC
  // does NOT bypass module licenses; an HR seat is required).
  const { hasAccess, loading: licenseLoading } = useModuleLicense('timesheet', 'hr');
  const [activeTab, setActiveTab] = useState<AdminTab>('register');

  const tabs: { key: AdminTab; label: string; icon: React.ReactNode }[] = [
    { key: 'register', label: 'Staff Register',      icon: <ClipboardList className="h-4 w-4" /> },
    { key: 'types',    label: 'Certification Types',  icon: <FileCheck className="h-4 w-4" />     },
  ];

  if (licenseLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="h-8 bg-neutral-100 rounded animate-pulse w-64 mb-4" />
        <div className="h-40 bg-neutral-100 rounded animate-pulse" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
          <Lock className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-neutral-800 mb-1">HR license required</h2>
          <p className="text-sm text-neutral-500 mb-4">
            The staff certifications register — managing certification types and
            recording certifications on behalf of staff — requires an active{' '}
            <span className="font-medium">Timesheet HR</span> licence seat.
          </p>
          <p className="text-xs text-neutral-400">
            Your organisation admin can purchase a seat via the Subscriptions page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="Staff Certifications"
        subtitle="Org-wide certifications & licenses register and type configuration"
        icon={<ClipboardList className="h-6 w-6 text-white" />}
        actionButtons={
          <Button variant="outline" size="sm" onClick={() => navigate('/inductions')}>
            <ShieldCheck className="h-4 w-4 mr-1" /> Inductions
          </Button>
        }
      />
      <div className="max-w-4xl mx-auto px-4 pt-4">
        <p className="rounded-lg border border-lime-200 bg-lime-50 px-3 py-2 text-sm text-lime-900 break-words">
          An equipment induction can require a certification from this register (a forklift licence, a driver licence,
          chemical accreditation) and can refuse anyone who has no current one on file. Set that on the induction.
        </p>
      </div>
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex flex-wrap gap-1 border-b border-neutral-200 mb-6">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs sm:gap-2 sm:px-4 sm:py-2.5 sm:text-sm rounded-t-md transition-colors whitespace-nowrap ${
              activeTab === tab.key
                ? 'bg-white border border-neutral-200 border-b-white -mb-px text-primary-600 font-medium'
                : 'text-neutral-500 hover:text-neutral-700'
            }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        <div className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-6">
          {activeTab === 'register' && <StaffRegisterTab />}
          {activeTab === 'types'    && <CertificationTypesTab />}
        </div>
      </div>
    </>
  );
}

// ============================================================================
// CERTIFICATION TYPES TAB
// Org-scoped catalog of general staff certification types, grouped by category.
// ============================================================================

interface TypeFormState {
  name: string;
  category: string;
  description: string;
  is_required: boolean;
  required_for: string;
}

const emptyTypeForm = (): TypeFormState => ({
  name: '', category: '', description: '', is_required: false, required_for: '',
});

function CertificationTypesTab() {
  const [types, setTypes] = useState<StaffCertificationType[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<StaffCertificationType | null>(null);
  const [form, setForm] = useState<TypeFormState>(emptyTypeForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const [typesRes, catsRes] = await Promise.all([
      listCertificationTypes({ module: 'general', includeInactive: true }),
      listCertificationCategories('general'),
    ]);
    if (typesRes.success) setTypes(typesRes.data);
    if (catsRes.success) setCategories(catsRes.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm(emptyTypeForm());
    setError(null);
    setShowModal(true);
  };

  const openEdit = (t: StaffCertificationType) => {
    setEditing(t);
    setForm({
      name: t.name,
      category: t.category || '',
      description: t.description || '',
      is_required: t.is_required,
      required_for: t.required_for || '',
    });
    setError(null);
    setShowModal(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category.trim() || null,
        description: form.description.trim() || null,
        is_required: form.is_required,
        required_for: form.is_required ? (form.required_for.trim() || null) : null,
      };
      if (editing) {
        const res = await updateCertificationType(editing.id, payload);
        if (!res.success) throw new Error('Update failed');
      } else {
        const res = await createCertificationType(payload);
        if (!res.success) throw new Error('Create failed');
      }
      setShowModal(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (t: StaffCertificationType) => {
    if (t.is_active) {
      if (!confirm(`Deactivate "${t.name}"? Existing records for it are kept.`)) return;
      await deactivateCertificationType(t.id);
    } else {
      await updateCertificationType(t.id, { is_active: true });
    }
    await load();
  };

  // Group types by category for a scannable list. Uncategorised first.
  const grouped = types.reduce<Map<string, StaffCertificationType[]>>((acc, t) => {
    const key = t.category || 'Uncategorised';
    const arr = acc.get(key) || [];
    arr.push(t);
    acc.set(key, arr);
    return acc;
  }, new Map());

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-neutral-900">Certification Types</h2>
          <p className="text-sm text-neutral-600">
            Define the certifications and licenses your staff can record
            (e.g. Forklift Licence, First Aid, Truck Licence, ACUP).
          </p>
        </div>
        <Button size="sm" onClick={openNew}>
          <Plus className="h-4 w-4 mr-1" /> Add type
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-neutral-500">Loading…</p>
      ) : types.length === 0 ? (
        <p className="text-sm text-neutral-500">No certification types defined yet.</p>
      ) : (
        <div className="space-y-5">
          {[...grouped.entries()].map(([category, items]) => (
            <div key={category}>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1">{category}</h3>
              <div className="divide-y divide-neutral-200">
                {items.map(t => (
                  <div key={t.id} className="flex items-center justify-between py-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${t.is_active ? 'text-neutral-900' : 'text-neutral-400 line-through'}`}>{t.name}</span>
                        {t.is_required && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                            Required{t.required_for ? ` · ${t.required_for}` : ''}
                          </span>
                        )}
                        {!t.is_active && (
                          <span className="text-xs bg-neutral-100 text-neutral-500 px-2 py-0.5 rounded-full">Inactive</span>
                        )}
                      </div>
                      {t.description && <p className="text-xs text-neutral-500 mt-0.5">{t.description}</p>}
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(t)} className="p-1.5 text-neutral-500 hover:text-primary-600" aria-label="Edit">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => toggleActive(t)} className="p-1.5 text-neutral-500 hover:text-rose-600" aria-label="Toggle active">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <header className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">{editing ? 'Edit' : 'Add'} certification type</h3>
              <button onClick={() => setShowModal(false)} className="text-neutral-400 hover:text-neutral-600"><X className="h-4 w-4" /></button>
            </header>
            <div className="px-5 py-4 space-y-3 max-h-[70vh] overflow-y-auto">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Name</label>
                <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                       className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
                       placeholder="e.g. Forklift Licence (LF)" />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Category (optional)</label>
                <input type="text" list="certification-categories" value={form.category}
                       onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                       className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
                       placeholder="e.g. Machinery, Safety, Driving" />
                <datalist id="certification-categories">
                  {categories.map(c => <option key={c} value={c} />)}
                </datalist>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Description (optional)</label>
                <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3}
                          className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm" />
              </div>
              <label className="flex items-center gap-2 text-sm text-neutral-700">
                <input type="checkbox" checked={form.is_required}
                       onChange={e => setForm(f => ({ ...f, is_required: e.target.checked }))} />
                Required certification
              </label>
              {form.is_required && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Required for (optional)</label>
                  <input type="text" value={form.required_for} maxLength={50}
                         onChange={e => setForm(f => ({ ...f, required_for: e.target.value }))}
                         className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
                         placeholder="e.g. All staff, Drivers, Operators" />
                </div>
              )}
              {error && <p className="text-sm text-rose-600">{error}</p>}
            </div>
            <footer className="px-5 py-4 border-t border-neutral-200 bg-neutral-50 rounded-b-xl flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowModal(false)} disabled={saving}>Cancel</Button>
              <Button onClick={save} isLoading={saving}>Save</Button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// STAFF REGISTER TAB — org-wide report grouped by user, with inline admin entry.
// HR can record or update a certification on a staff member's behalf.
// ============================================================================

function StaffRegisterTab() {
  const orgTz = useOrgTimezone();
  const [rows, setRows] = useState<CertificationReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<CertificationReportRow | null>(null);
  const [form, setForm] = useState({
    credential_number: '',
    issuer: '',
    issued_date: '',
    expiry_date: '',
    notes: '',
  });
  const [docFile, setDocFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    const res = await getCertificationReport('any');
    if (res.success) setRows(res.data);
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    load();
  }, []);

  const openEdit = (row: CertificationReportRow) => {
    setEditing(row);
    setForm({
      credential_number: row.credential_number || '',
      issuer: row.issuer || '',
      issued_date: row.issued_date || '',
      expiry_date: row.expiry_date || '',
      notes: '',
    });
    setDocFile(null);
    setError(null);
  };

  const closeModal = () => {
    setEditing(null);
    setError(null);
    setDocFile(null);
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    setError(null);
    try {
      if (editing.certification_id) {
        const res = await updateCertification(editing.certification_id, {
          credential_number: form.credential_number,
          issuer: form.issuer,
          issued_date: form.issued_date || undefined,
          expiry_date: form.expiry_date || undefined,
          notes: form.notes,
          document: docFile,
        });
        if (!res.success) throw new Error('Update failed');
      } else {
        const res = await createCertification({
          certification_type_id: editing.certification_type_id,
          user_id: editing.user_id,
          credential_number: form.credential_number || undefined,
          issuer: form.issuer || undefined,
          issued_date: form.issued_date || undefined,
          expiry_date: form.expiry_date || undefined,
          notes: form.notes || undefined,
          document: docFile,
        });
        if (!res.success) throw new Error('Create failed');
      }
      closeModal();
      setLoading(true);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Group by user so the register is scannable.
  const grouped = rows.reduce<Map<number, { user: CertificationReportRow; entries: CertificationReportRow[] }>>((acc, r) => {
    const existing = acc.get(r.user_id);
    if (existing) existing.entries.push(r);
    else acc.set(r.user_id, { user: r, entries: [r] });
    return acc;
  }, new Map());

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-neutral-900">Staff register</h2>
        <p className="text-sm text-neutral-600">
          Certification status for every active staff member. Use the action column
          to record or update a certification on a staff member's behalf.
        </p>
      </div>
      {loading ? (
        <p className="text-sm text-neutral-500">Loading…</p>
      ) : grouped.size === 0 ? (
        <p className="text-sm text-neutral-500">No active staff or certification types yet.</p>
      ) : (
        <div className="space-y-4">
          {[...grouped.values()].map(({ user, entries }) => (
            <div key={user.user_id} className="border border-neutral-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-neutral-900">
                  {[user.first_name, user.last_name].filter(Boolean).join(' ') || user.username}
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase text-neutral-500">
                      <th className="text-left py-1">Certification</th>
                      <th className="text-left py-1">Number</th>
                      <th className="text-left py-1">Expires</th>
                      <th className="text-left py-1">Status</th>
                      <th className="text-right py-1" />
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map(e => (
                      <tr key={`${e.user_id}-${e.certification_type_id}`} className="border-t border-neutral-100">
                        <td className="py-1.5">
                          {e.type_category && <span className="text-neutral-400">{e.type_category} · </span>}
                          {e.type_name}
                          {e.type_is_required && <span className="text-xs text-amber-600 ml-1">(required)</span>}
                        </td>
                        <td className="py-1.5 text-neutral-700">{e.credential_number || '—'}</td>
                        <td className="py-1.5 text-neutral-700">{e.expiry_date ? formatLocalDateInTz(e.expiry_date, orgTz) : '—'}</td>
                        <td className="py-1.5"><CredentialStatusBadge status={e.status} /></td>
                        <td className="py-1.5 text-right">
                          <button
                            onClick={() => openEdit(e)}
                            className="text-xs text-primary-600 hover:underline whitespace-nowrap"
                          >
                            {e.certification_id ? 'Edit' : 'Record'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <header className="px-5 py-4 border-b border-neutral-200 flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold">
                  {editing.certification_id ? 'Edit certification' : 'Record certification'}
                </h3>
                <p className="text-xs text-neutral-500 mt-0.5">
                  {editing.type_name} · {[editing.first_name, editing.last_name].filter(Boolean).join(' ') || editing.username}
                </p>
              </div>
              <button onClick={closeModal} className="text-neutral-400 hover:text-neutral-600"><X className="h-4 w-4" /></button>
            </header>
            <div className="px-5 py-4 space-y-3 max-h-[70vh] overflow-y-auto">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Credential number</label>
                <input type="text" value={form.credential_number}
                       onChange={e => setForm(f => ({ ...f, credential_number: e.target.value }))}
                       className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Issuer</label>
                <input type="text" value={form.issuer}
                       onChange={e => setForm(f => ({ ...f, issuer: e.target.value }))}
                       className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Issued</label>
                  <input type="date" value={form.issued_date}
                         onChange={e => setForm(f => ({ ...f, issued_date: e.target.value }))}
                         className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Expires</label>
                  <input type="date" value={form.expiry_date}
                         onChange={e => setForm(f => ({ ...f, expiry_date: e.target.value }))}
                         className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Notes</label>
                <textarea value={form.notes} rows={2}
                          onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                          className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm resize-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Document (optional)</label>
                <input type="file" accept="application/pdf,image/*"
                       onChange={e => setDocFile(e.target.files?.[0] || null)}
                       className="w-full text-sm" />
                {editing.certification_id && editing.has_document && !docFile && (
                  <p className="text-xs text-neutral-500 mt-1">
                    Current: {editing.document_original_name || 'document on file'} (upload a new file to replace)
                  </p>
                )}
              </div>
              {error && <p className="text-sm text-rose-600">{error}</p>}
            </div>
            <footer className="px-5 py-4 border-t border-neutral-200 bg-neutral-50 rounded-b-xl flex justify-end gap-2">
              <Button variant="outline" onClick={closeModal} disabled={saving}>Cancel</Button>
              <Button onClick={save} isLoading={saving}>Save</Button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

export default CertificationsAdmin;
